/**
 * Events API Service
 *
 * Provides API methods for event management
 */

import { api } from '@/lib/apiClient';
import type { Event, EventAttachment, CreateEventPayload, UpdateEventPayload, EventFilter } from '@flock-types/events.types';
import type { CalendarEvent } from '@/features/events/calendar/types';

/**
 * Pin a request to a specific organization (cross-org calendar). The backend
 * verifies membership of the org named in the header, so this is safe for any
 * org the user belongs to. Omitted → apiClient falls back to the current org.
 */
function orgConfig(organizationId?: string) {
  return organizationId ? { headers: { 'X-Organization-Id': organizationId } } : undefined;
}

/**
 * API response format for events list
 */
interface EventsListResponse {
  success: boolean;
  data: Event[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
  };
}

/**
 * API response format for single event
 */
interface EventResponse {
  success: boolean;
  data: Event;
}

/**
 * API response format for event creation/update
 */
interface EventMutationResponse {
  success: boolean;
  data: Event;
  message?: string;
}

/**
 * Events API client
 */
export const eventsService = {
  /**
   * Get list of events for a group
   * @param groupId - Group ID to fetch events for
   * @param filter - Optional filter: 'upcoming' or 'past'
   * @returns Array of events
   */
  async getEvents(groupId: string, filter?: EventFilter): Promise<Event[]> {
    const params = new URLSearchParams();

    // Convert filter to date range (if provided)
    if (filter) {
      const now = new Date();
      if (filter === 'upcoming') {
        params.set('startDate', now.toISOString());
      } else if (filter === 'past') {
        params.set('endDate', now.toISOString());
      }
    }

    // Include RSVP counts
    params.set('includeRsvp', 'true');

    const queryString = params.toString();
    const url = `/events/groups/${groupId}/events${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<EventsListResponse>(url);
    return response.data;
  },

  /**
   * Get events across all visible groups for a date range (calendar view).
   * RLS scopes results to groups the user can see.
   * @param startDate - ISO start of range (inclusive, matched on start_time)
   * @param endDate - ISO end of range (inclusive)
   * @param groupIds - Optional group filter; omit for all visible groups
   */
  async getCalendarEvents(
    startDate: string,
    endDate: string,
    groupIds?: string[],
    organizationId?: string
  ): Promise<CalendarEvent[]> {
    const params = new URLSearchParams({ startDate, endDate });
    if (groupIds && groupIds.length > 0) params.set('groupIds', groupIds.join(','));
    const response = await api.get<{ success: boolean; data: CalendarEvent[]; count: number }>(
      `/events/calendar/view?${params.toString()}`,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /**
   * Send a manual reminder email (one-tap RSVP links) for an event.
   * Leader-only; the backend checks per-group permission.
   * @param eventId - Event ID
   * @param options.audience - 'non_responders' nudges only people who haven't replied
   */
  async sendReminder(
    eventId: string,
    options?: {
      reminderType?: 'all' | 'email' | 'sms';
      audience?: 'all' | 'non_responders';
      organizationId?: string;
    }
  ): Promise<{ sentCount?: number }> {
    const response = await api.post<{
      success: boolean;
      data?: { sentCount?: number };
    }>(
      `/events/${eventId}/remind`,
      {
        reminderType: options?.reminderType ?? 'all',
        audience: options?.audience ?? 'all',
      },
      orgConfig(options?.organizationId)
    );
    return response.data ?? {};
  },

  /**
   * Get single event by ID
   * @param eventId - Event ID
   * @returns Event details
   */
  async getEvent(eventId: string, organizationId?: string): Promise<Event> {
    const response = await api.get<EventResponse>(`/events/${eventId}`, orgConfig(organizationId));
    return response.data;
  },

  /**
   * Create a new event
   * @param payload - Event creation data
   * @returns Created event
   */
  async createEvent(payload: CreateEventPayload): Promise<Event> {
    const response = await api.post<EventMutationResponse>('/events', payload);
    return response.data;
  },

  /**
   * Update an existing event
   * @param payload - Event update data (must include id)
   * @returns Updated event
   */
  async updateEvent(payload: UpdateEventPayload, organizationId?: string): Promise<Event> {
    const { id, ...updateData } = payload;
    const response = await api.put<EventMutationResponse>(
      `/events/${id}`,
      updateData,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /**
   * Delete an event
   * @param eventId - Event ID to delete
   */
  async deleteEvent(eventId: string, organizationId?: string): Promise<void> {
    await api.delete(`/events/${eventId}`, orgConfig(organizationId));
  },

  /**
   * Submit an RSVP for an event
   * @param eventId - Event ID
   * @param status - RSVP status: 'yes', 'maybe', or 'no'
   * @returns RSVP data
   */
  async submitRSVP(
    eventId: string,
    status: 'yes' | 'maybe' | 'no',
    organizationId?: string
  ): Promise<{
    id: string;
    status: string;
    responded_at: string;
  }> {
    const response = await api.post<{
      success: boolean;
      data: { id: string; status: string; responded_at: string };
    }>(`/events/${eventId}/rsvp`, { status }, orgConfig(organizationId));
    return response.data;
  },

  /**
   * Get all RSVPs for an event with user details
   * @param eventId - Event ID
   * @returns RSVPs with user names and counts
   */
  async getEventRSVPs(eventId: string, organizationId?: string): Promise<{
    rsvps: Array<{
      id: string;
      user_id: string;
      user_name: string;
      user_email: string;
      status: 'yes' | 'maybe' | 'no' | 'not_responded';
      responded_at: string;
      response_method: string;
    }>;
    counts: {
      yes: number;
      maybe: number;
      no: number;
      not_responded: number;
    };
    eventTitle: string;
    attendanceTaken: boolean;
    attendance: Array<{
      id: string;
      user_id: string;
      user_name: string;
      user_email: string;
      attended: boolean;
      attendance_type: string;
      attended_at: string;
      notes: string | null;
      status: 'present' | 'absent';
    }> | null;
    attendanceCounts: {
      present: number;
      absent: number;
      total: number;
    } | null;
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        rsvps: Array<{
          id: string;
          user_id: string;
          user_name: string;
          user_email: string;
          status: 'yes' | 'maybe' | 'no' | 'not_responded';
          responded_at: string;
          response_method: string;
        }>;
        counts: {
          yes: number;
          maybe: number;
          no: number;
          not_responded: number;
        };
        eventTitle: string;
        attendanceTaken: boolean;
        attendance: Array<{
          id: string;
          user_id: string;
          user_name: string;
          user_email: string;
          attended: boolean;
          attendance_type: string;
          attended_at: string;
          notes: string | null;
          status: 'present' | 'absent';
        }> | null;
        attendanceCounts: {
          present: number;
          absent: number;
          total: number;
        } | null;
      };
    }>(`/events/${eventId}/rsvps`, orgConfig(organizationId));
    return response.data;
  },

  /**
   * Get current user's RSVPs across all events
   * @param filter - Optional filter by status
   * @returns User's RSVPs with event details
   */
  async getUserRSVPs(filter?: 'yes' | 'maybe' | 'no'): Promise<Array<{
    event_id: string;
    event_title: string;
    event_start_time: string;
    event_end_time: string;
    event_location: string;
    group_id: string;
    group_name: string;
    rsvp_status: 'yes' | 'maybe' | 'no' | 'not_responded';
    rsvp_responded_at: string;
  }>> {
    const params = new URLSearchParams();
    if (filter) params.append('status', filter);
    const queryString = params.toString();
    const url = `/events/my-rsvps${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data: {
        rsvps: Array<{
          event_id: string;
          event_title: string;
          event_start_time: string;
          event_end_time: string;
          event_location: string;
          group_id: string;
          group_name: string;
          rsvp_status: 'yes' | 'maybe' | 'no' | 'not_responded';
          rsvp_responded_at: string;
        }>;
      };
    }>(url);
    return response.data.rsvps;
  },

  // ============================================================================
  // ATTACHMENT METHODS
  // ============================================================================

  /**
   * Get a presigned URL for uploading an attachment
   * @param eventId - Event ID
   * @param fileName - Name of the file to upload
   * @param contentType - MIME type of the file
   * @param fileSize - Size of the file in bytes
   * @returns Upload URL and metadata
   */
  async getAttachmentUploadUrl(
    eventId: string,
    fileName: string,
    contentType: string,
    fileSize: number,
    organizationId?: string
  ): Promise<{
    uploadUrl: string;
    downloadUrl: string;
    fileId: string;
    storagePath: string;
    fileName: string;
    originalFileName: string;
    contentType: string;
    fileSize: number;
    expiresAt: string;
  }> {
    const response = await api.post<{
      success: boolean;
      data: {
        uploadUrl: string;
        downloadUrl: string;
        fileId: string;
        storagePath: string;
        fileName: string;
        originalFileName: string;
        contentType: string;
        fileSize: number;
        expiresAt: string;
      };
    }>(
      `/events/${eventId}/attachments/presigned`,
      {
        fileName,
        contentType,
        fileSize,
      },
      orgConfig(organizationId)
    );
    return response.data;
  },

  /**
   * Record an uploaded attachment in the database
   * Call this after successfully uploading to R2
   * @param eventId - Event ID
   * @param attachmentData - Metadata from getAttachmentUploadUrl response
   * @returns Created attachment record
   */
  async recordAttachment(
    eventId: string,
    attachmentData: {
      fileName: string;
      originalFileName: string;
      fileSize: number;
      contentType: string;
      storagePath: string;
      downloadUrl: string;
    },
    organizationId?: string
  ): Promise<EventAttachment> {
    const response = await api.post<{
      success: boolean;
      data: EventAttachment;
    }>(`/events/${eventId}/attachments`, attachmentData, orgConfig(organizationId));
    return response.data;
  },

  /**
   * Get all attachments for an event
   * @param eventId - Event ID
   * @returns List of attachments
   */
  async getAttachments(eventId: string, organizationId?: string): Promise<EventAttachment[]> {
    const response = await api.get<{
      success: boolean;
      data: EventAttachment[];
    }>(`/events/${eventId}/attachments`, orgConfig(organizationId));
    return response.data;
  },

  /**
   * Delete an attachment
   * @param eventId - Event ID
   * @param attachmentId - Attachment ID to delete
   */
  async deleteAttachment(
    eventId: string,
    attachmentId: string,
    organizationId?: string
  ): Promise<void> {
    await api.delete(`/events/${eventId}/attachments/${attachmentId}`, orgConfig(organizationId));
  },

  /**
   * Get storage usage for the organization
   * @returns Storage usage info
   */
  async getStorageUsage(): Promise<{
    used_bytes: number;
    max_bytes: number | null;
    tier_code: string;
    percentage: number;
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        used_bytes: number;
        max_bytes: number | null;
        tier_code: string;
        percentage: number;
      };
    }>('/events/storage/usage');
    return response.data;
  },
};

export default eventsService;
